import { useState, useEffect } from "react";
import { useAuth } from "../../../../hooks/useAuth";
import toast from "react-hot-toast";
import "./TicketsTable.css";
import Ticket from "./Ticket";
import { getMyTickets, getAllTickets } from "../../../../api/ticketApi";

/**
 * Tickets Table Component
 * Updated for Auth Microservice integration - v2.0
 * Shows user's tickets or all tickets (for SUPERADMIN)
 */
const TicketsTable = ({ showAll = false, view = "all" }) => {
    const { isSuperAdmin } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        pages: 0,
    });
    const [filters, setFilters] = useState({
        status: "",
        priority: "",
        search: "",
    });

    useEffect(() => {
        fetchTickets(page, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const fetchTickets = async (pageNum, filterParams = {}) => {
        try {
            setLoading(true);

            const params = {
                page: pageNum,
                limit: 10,
                ...filterParams,
            };

            // Use different endpoint based on showAll prop
            const response = showAll 
                ? await getAllTickets(params)
                : await getMyTickets(params);

            if (response.success) {
                    setTickets(response.data.tickets);
                setPagination(response.data.pagination);
            } else {
                toast.error(response.message || "Failed to load tickets");
            }
        } catch (error) {
            console.error("Error fetching tickets:", error);
            toast.error("Failed to load tickets");
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
        setTickets([]);
        fetchTickets(1, newFilters);
    };

    const goToPage = (pageNum) => {
        setPage(pageNum);
        setTickets([]);
        fetchTickets(pageNum, filters);
    };

    return (
        <div style={{ padding: "0" }}>
            {/* Modern Filters */}
            <div style={{ 
                padding: "20px 24px", 
                borderBottom: "1px solid #e9ecef",
                background: "#f8f9fa"
            }}>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>

                <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
                        style={{ 
                            padding: "8px 12px", 
                            borderRadius: "6px", 
                            border: "1px solid #dee2e6",
                            fontSize: "13px",
                            minWidth: "130px",
                            background: "white",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                        }}
                >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                </select>

                <select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange({ ...filters, priority: e.target.value })}
                        style={{ 
                            padding: "8px 12px", 
                            borderRadius: "6px", 
                            border: "1px solid #dee2e6",
                            fontSize: "13px",
                            minWidth: "130px",
                            background: "white",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                        }}
                >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                </select>

                <input
                    type="text"
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyPress={(e) => {
                        if (e.key === "Enter") {
                            handleFilterChange(filters);
                        }
                    }}
                        style={{ 
                            padding: "8px 12px", 
                            borderRadius: "6px", 
                            border: "1px solid #dee2e6", 
                            flex: 1,
                            fontSize: "13px",
                            minWidth: "200px",
                            background: "white",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                        }}
                />

                <button
                    onClick={() => handleFilterChange(filters)}
                    style={{
                        padding: "8px 16px",
                            background: "linear-gradient(135deg, #3bac75 0%, #2d8a5a 100%)",
                        color: "white",
                        border: "none",
                            borderRadius: "6px",
                        cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                            boxShadow: "0 2px 4px rgba(59, 172, 117, 0.3)"
                    }}
                >
                    <i className="las la-search" /> Search
                </button>
                </div>
            </div>

            {/* Modern Table */}
            <div style={{ overflow: "hidden" }}>
                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    margin: 0
                }}>
                    <thead style={{ background: "#f8f9fa" }}>
                        <tr>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>#</th>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Ticket #</th>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Subject</th>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Creator</th>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Priority</th>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Status</th>
                            <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Created</th>
                            {showAll && isSuperAdmin && <th style={{
                                padding: "16px 20px",
                                textAlign: "left",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6"
                            }}>Assigned To</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.map((ticket, index) => {
                            return (
                                <Ticket
                                    key={ticket.id}
                                    ticket={ticket}
                                    index={(page - 1) * 10 + index}
                                    showAssignee={showAll && isSuperAdmin}
                                />
                            );
                        })}
                    </tbody>
                </table>
                {tickets.length === 0 && !loading && (
                    <div style={{ 
                        textAlign: "center", 
                        padding: "60px 20px", 
                        color: "#6c757d",
                        background: "#f8f9fa"
                    }}>
                        <i className="las la-inbox" style={{ 
                            fontSize: "48px", 
                            display: "block", 
                            marginBottom: "16px",
                            color: "#adb5bd"
                        }} />
                        <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#495057" }}>No tickets found</h3>
                        <p style={{ fontSize: "14px", margin: 0 }}>Create your first ticket to get started!</p>
                    </div>
                )}
            </div>

            {/* Modern Pagination */}
            {tickets.length > 0 && pagination.pages > 1 && (
                <div style={{ 
                    padding: "20px 24px", 
                    borderTop: "1px solid #e9ecef",
                    background: "#f8f9fa",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>
                        Showing {((pagination.page - 1) * 10) + 1} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} tickets
                    </div>
                    
                    {loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <i className="las la-spinner la-spin" style={{ 
                                fontSize: "16px", 
                                color: "#3bac75"
                            }} />
                            <span style={{ fontSize: "14px", color: "#6c757d" }}>Loading...</span>
                </div>
            ) : (
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            {/* Previous Button */}
                            <button
                                onClick={() => goToPage(page - 1)}
                                disabled={page === 1}
                                style={{
                                    padding: "8px 12px",
                                    background: page === 1 ? "#e9ecef" : "white",
                                    color: page === 1 ? "#adb5bd" : "#495057",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "6px",
                                    cursor: page === 1 ? "not-allowed" : "pointer",
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    transition: "all 0.2s"
                                }}
                            >
                                <i className="las la-chevron-left" />
                            </button>

                            {/* Page Numbers */}
                            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                let pageNum;
                                if (pagination.pages <= 5) {
                                    pageNum = i + 1;
                                } else if (page <= 3) {
                                    pageNum = i + 1;
                                } else if (page >= pagination.pages - 2) {
                                    pageNum = pagination.pages - 4 + i;
                                } else {
                                    pageNum = page - 2 + i;
                                }

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => goToPage(pageNum)}
                                        style={{
                                            padding: "8px 12px",
                                            background: page === pageNum ? "#3bac75" : "white",
                                            color: page === pageNum ? "white" : "#495057",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            fontWeight: "500",
                                            transition: "all 0.2s",
                                            minWidth: "36px"
                                        }}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            {/* Next Button */}
                            <button
                                onClick={() => goToPage(page + 1)}
                                disabled={page === pagination.pages}
                                style={{
                                    padding: "8px 12px",
                                    background: page === pagination.pages ? "#e9ecef" : "white",
                                    color: page === pagination.pages ? "#adb5bd" : "#495057",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "6px",
                                    cursor: page === pagination.pages ? "not-allowed" : "pointer",
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    transition: "all 0.2s"
                                }}
                            >
                                <i className="las la-chevron-right" />
                            </button>
                    </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TicketsTable;
